import type { GameweekWindowEntry, TickerSortBy } from './types';
import type { TeamFixturePreview } from '@api-service/team-fixtures-preview';
import { GAMEWEEK_ONE, MAX_GAMEWEEK } from '@lib/constants';

// A blank gameweek is neither an easy run nor a hard one, so it takes the mid
// rating; scoring it 0 would rank a team with a blank as the easiest.
const BLANK_GAMEWEEK_RATING = 5;
const NO_DIFFICULTY_RATING = 5;

export type TickerRow = {
  shortName: string;
  name: string;
  gameweeks: GameweekWindowEntry[];
};

export const buildFixtureRange = (
  fixtures: TeamFixturePreview[],
  startGameweek: number,
  count: number
): GameweekWindowEntry[] => {
  const start = Math.min(Math.max(startGameweek, GAMEWEEK_ONE), MAX_GAMEWEEK);
  const end = Math.min(MAX_GAMEWEEK, start + count - 1);
  const entries: GameweekWindowEntry[] = [];
  for (let gameweek = start; gameweek <= end; gameweek += 1) {
    entries.push({
      gameweek,
      fixtures: fixtures.filter((fixture) => fixture.gameweek === gameweek)
    });
  }
  return entries;
};

export const averageDifficulty = (gameweeks: GameweekWindowEntry[]): number => {
  if (gameweeks.length === 0) return BLANK_GAMEWEEK_RATING;
  const total = gameweeks.reduce((sum, { fixtures }) => {
    if (fixtures.length === 0) return sum + BLANK_GAMEWEEK_RATING;
    const ratings = fixtures.map(
      (fixture) =>
        fixture.inferences?.difficulty.overall ?? NO_DIFFICULTY_RATING
    );
    return sum + ratings.reduce((a, b) => a + b, 0) / ratings.length;
  }, 0);
  return total / gameweeks.length;
};

export const sortTickerRows = (
  rows: TickerRow[],
  sortBy: TickerSortBy
): TickerRow[] => {
  if (sortBy === 'selected') return rows;
  if (sortBy === 'alphabetical') {
    return [...rows].sort((a, b) => a.shortName.localeCompare(b.shortName));
  }
  return [...rows].sort(
    (a, b) =>
      averageDifficulty(a.gameweeks) - averageDifficulty(b.gameweeks) ||
      a.shortName.localeCompare(b.shortName)
  );
};
